import AppSettingsService from "./AppSettingsService";
import {
  AccountInfo,
  Configuration,
  AuthenticationResult,
  PublicClientApplication,
  SilentRequest,
  RedirectRequest,
  EndSessionRequest,
  LogLevel,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";

import { User } from "../Models/User";
import { noop } from "../Helpers/Utilities";
export interface AuthProps {
	authService: IAuthService;
}
export interface IAuthService {
  SignIn: () => void;
  SignOut: () => void;
  getToken: () => Promise<string>;
  handlePageLoadEvent: () => Promise<void> ;
}
class AuthService implements IAuthService {
  constructor(appSettings: AppSettingsService) {
    if (!appSettings) {
      throw new Error("the app settings service was not provided");
    }

    this.appSettings = appSettings;

    const msalConfig = this.GetMsalClientConfiguration();
    this.msalApplication = new PublicClientApplication(msalConfig);
  }
  
  userSignedIn: (user: Partial<User>) => void = noop;
  userSignedOut: () => void = noop;

  // msal application object
  msalApplication: PublicClientApplication;

  // settings service
  appSettings: AppSettingsService;

  // cached account info
  account: AccountInfo | null = null;

  getRedirectRequest = (): RedirectRequest => {
    const redirectRequest: RedirectRequest = {
      scopes: [this.appSettings.GetMsalClientScope()],
      prompt: "select_account",
    };
    return redirectRequest;
  }
  

  isAuthenticated = (): boolean => {
    return this.account !== undefined && this.account !== null;
  };
  handlePageLoadEvent = (): Promise<void> => {
    // let exceptions bubble up to the caller to handle
    return this.msalApplication.handleRedirectPromise().then((authResult: AuthenticationResult | null) => {
      this.handleRedirectResponse(authResult);
    });
  };

  handleRedirectResponse = async (authResult: AuthenticationResult | null) => {
    // if this page load is redirect from the Microsoft Identity platform then the
    // authResult will be populated. Otherwise null on other page loads.

    if (authResult !== null) {
      // save the fresh account info from the result.
      this.account = authResult.account;
    } else {
      // see if we have cached accounts.
      const currentAccounts = this.msalApplication.getAllAccounts();
      
      if (currentAccounts === null || currentAccounts.length < 1) {
        // no cached accounts.
        // user will need to click the sign-in button and redirect to login.
        //return;
        this.SignIn();
        return;
        
      } else if (currentAccounts.length > 1) {
        // there are some situations where the user may have multiple (different) cached logins.
        // this conditional block would need to be updated to support multiple accounts.
        // otherwise it will just grab the first one below.
        // TODO: What do we want to do in this case?
        console.warn("Multiple accounts detected in MSAL account cache.");
        this.account = currentAccounts[0];       
      } else if (currentAccounts.length === 1) {
        // we have exactly 1 cached account.
        // set the account info. user may not need to sign in.        
        this.account = currentAccounts[0];  
        // await this.getToken();   
			}
		}
		if(this.account) {
			this.userSignedIn({
				userName: this.account.username,
				firstName: this.account.name || "",
				lastName: "",
      });    
		}
  };

  GetMsalClientConfiguration = (): Configuration => {
    return {
      auth: {
        clientId: this.appSettings.GetMsalClientId(),
        authority: this.appSettings.GetMsalTenantAuthorityUri(),
        redirectUri: this.appSettings.GetLoginRedirectUri(),
      },
      cache: {
        cacheLocation: this.appSettings.GetMsalCacheLocation(),
        storeAuthStateInCookie: this.appSettings.GetMsalStoreAuthInCookie(),
      },
      system: {
        loadFrameTimeout: 30000,
        tokenRenewalOffsetSeconds: 60,
        loggerOptions: {
          loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
          },
          piiLoggingEnabled: false,
          logLevel: LogLevel.Verbose,
        },
      },
    };
  };

  getToken = async(): Promise<string> => {
    const tokenRequest: SilentRequest = {
      account: this.account 
        ? this.account as AccountInfo 
        : this.msalApplication.getAllAccounts() && this.msalApplication.getAllAccounts().length > 0
          ? this.msalApplication.getAllAccounts()[0] 
          : {} as AccountInfo, // This will and should fail 
      scopes: [this.appSettings.GetMsalClientScope(), "User.Read"],
    };

    // msal will return the cached token if present, or call to get a new one
    // if it is expired or near expiring.
    const tokenResponse = await this.msalApplication.acquireTokenSilent(tokenRequest)
      .catch(error => {
        if(error instanceof InteractionRequiredAuthError) {          
          // console.log('aquiring new token');
          return this.msalApplication.acquireTokenRedirect(this.getRedirectRequest());
        } else {
          console.log("Token Error: ", error);
        }
      });
    
      if((tokenResponse as AuthenticationResult)?.accessToken) {
        return Promise.resolve((tokenResponse as AuthenticationResult).accessToken);
      } else {
        // NOTE: If this is the case the user should already be redirected to the login screen or getting a refresh token
        return Promise.resolve("error");
      }
  };

  SignIn = () => {
    // this will redirect the web application to the Microsoft Identity platform sign in pages.
    // no code will execute after this point.
    this.msalApplication.loginRedirect(this.getRedirectRequest());
  };

  SignOut = () => {
    if (!this.account) {
      // no cached login to signout
      return;
    }

    const accountInfo: AccountInfo | null = this.msalApplication.getAccountByUsername(this.account?.username as string);

    if (accountInfo !== null) {
      const logoutRequestPayload: EndSessionRequest = {
        account: accountInfo,
      };

      this.msalApplication.logout(logoutRequestPayload);
		}
		this.userSignedOut();
  };
}

export default AuthService;


export const MockAuthService: IAuthService = {
  SignIn: noop,
  SignOut: noop,
  getToken: () => Promise.resolve(""),
  handlePageLoadEvent: () => Promise.resolve(),
}